import { Injectable } from '@angular/core';
import {Observable, ReplaySubject} from "rxjs";
import {Dictionary} from "../interface/dictionary";
import {DynamicDictionary} from "../interface/dynamic-dictionary";
import {HttpClient} from "@angular/common/http";
import {first, take} from "rxjs/operators";
import {ListResponse} from "../interface/response/list-response";
import {CountedAndPaginated} from "../interface/response/counted-and-paginated";
import {Tag} from "../interface/tag";
import {FilterService} from "./filter.service";
import {Uuid} from "../interface/response/uuid";
import {AddressImport} from "../interface/address-import";
import {DynamicDictionaryImport} from "../interface/dynamic-dictionary-import";

@Injectable({
  providedIn: 'root'
})
export class DynamicDictService {
  loaded = false;

  private readonly dynamicDictSubject: ReplaySubject<Array<DynamicDictionary>> = new ReplaySubject();
  public readonly dynamicDictionaryListSubject: ReplaySubject<CountedAndPaginated<DynamicDictionary>>
    = new ReplaySubject<CountedAndPaginated<DynamicDictionary>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
  ) {
    this.loadDynamicDict();
  }

  public loadDynamicDict(): void {
    this.loaded = true;
    this.httpClient.get('/rest/crm/dynamicDictionary')
      .pipe(first())
      .subscribe((response: ListResponse<DynamicDictionary>) => {
        this.dynamicDictSubject.next(response.items);
      });
  }
  public getDynamicDictSubject(): ReplaySubject<Array<DynamicDictionary>> {
    return this.dynamicDictSubject;
  }

  private loadIfNotLoaded(): void {
    if (!this.loaded) {
      this.loadDynamicDict();
    }
  }

  public getKey(id: number): Observable<string> {
    this.loadIfNotLoaded();
    const keyObservable: ReplaySubject<string> = new ReplaySubject<string>(1);

    this.dynamicDictSubject.pipe(first())
      .subscribe((items: DynamicDictionary[]) => {
        items.some((el: DynamicDictionary) => {
          if (el.id === id) {
            keyObservable.next(el.type.key);
            keyObservable.complete();
            return true;
          }

          return false;
        });

        keyObservable.next('');
        keyObservable.complete();
      });

    return keyObservable.pipe(first());
  }

  public getLoaded(id: number): Observable<DynamicDictionary> {
    this.loadIfNotLoaded();
    const dictObservable: ReplaySubject<DynamicDictionary> = new ReplaySubject<DynamicDictionary>(1);

    this.dynamicDictSubject.pipe(first())
      .subscribe((items: DynamicDictionary[]) => {
        items.some((el: DynamicDictionary) => {
          if (el.id === id) {
            dictObservable.next(el);
            dictObservable.complete();
            return true;
          }

          return false;
        });

        dictObservable.next(null);
        dictObservable.complete();
      });

    return dictObservable.pipe(first());
  }

  public get(id: any): Observable<DynamicDictionary> {
    return this.httpClient.get(`/rest/crm/dynamicDictionary/${id}`)
      .pipe(first()) as Observable<DynamicDictionary>;
  }

  public loadList(page: number, pageSize: number, filters: object,): void {
    this.httpClient.get(`/rest/crm/dynamicDictionary-list?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<DynamicDictionary>) => {
        this.dynamicDictionaryListSubject.next(result);
      });
  }

  public getDynamicDictionaryListSubject(): ReplaySubject<CountedAndPaginated<DynamicDictionary>> {
    return this.dynamicDictionaryListSubject;
  }

  public sendForm(formData: object, id?: string): Observable<any> {
    if (id) {
      return this.httpClient.put(`/rest/crm/dynamicDictionary/${id}`, formData)
        .pipe(
          take(1)
        ) as Observable<any>;
    } else {
      return this.httpClient.post('/rest/crm/dynamicDictionary', formData)
        .pipe(
          take(1)
        ) as Observable<any>;
    }
  }

  public downloadReport(filters: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/dynamicDictionary/method/report?` + this.filterService.parseFilters(filters), {})
      .pipe(first()) as Observable<Uuid>;
  }

  public import(formData: object): Observable<Uuid> {
    return this.httpClient.post(`/rest/crm/dynamicDictionary/method/import`, formData)
      .pipe(
        take(1)
      ) as Observable<Uuid>;
  }

  public getImport(importId: string): Observable<DynamicDictionaryImport> {
    return this.httpClient.get(`/rest/crm/dynamicDictionary/import/${importId}`)
      .pipe(first()) as Observable<DynamicDictionaryImport>;
  }

  public delete(id: number): Observable<any> {
    return this.httpClient.delete(`/rest/crm/dynamicDictionary/${id}`)
      .pipe(first());
  }
}
